import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw } from "lucide-react";
import {
  AssetStatus,
  AssetCondition,
  AssetCategoryView,
  AssetLocationView,
} from "../../../../api/General/assets";
import { Tooltip } from "@/components/ui/Tooltip";

interface AssetFiltersProps {
  search: string;
  onSearchChange: (val: string) => void;
  categoryL1Id: number | undefined;
  onCategoryL1Change: (val: number | undefined) => void;
  categoryL2Id: number | undefined;
  onCategoryL2Change: (val: number | undefined) => void;
  categoryL3Id: number | undefined;
  onCategoryL3Change: (val: number | undefined) => void;
  status: AssetStatus | undefined;
  onStatusChange: (val: AssetStatus | undefined) => void;
  condition: AssetCondition | undefined;
  onConditionChange: (val: AssetCondition | undefined) => void;
  categories: AssetCategoryView[];
  locationId: number | undefined;
  onLocationChange: (val: number | undefined) => void;
  locations: AssetLocationView[];
  onReset: () => void;
}

const AssetFilters: React.FC<AssetFiltersProps> = ({
  search,
  onSearchChange,
  categoryL1Id,
  onCategoryL1Change,
  categoryL2Id,
  onCategoryL2Change,
  categoryL3Id,
  onCategoryL3Change,
  status,
  onStatusChange,
  condition,
  onConditionChange,
  categories,
  locationId,
  onLocationChange,
  locations,
  onReset,
}) => {
  const { t } = useTranslation();

  const l1s = useMemo(() => categories.filter((c) => c.level === 1), [categories]);
  const l2s = useMemo(() => categories.filter((c) => c.level === 2 && c.parentId === categoryL1Id), [categories, categoryL1Id]);
  const l3s = useMemo(() => categories.filter((c) => c.level === 3 && c.parentId === categoryL2Id), [categories, categoryL2Id]);

  // Picking a broader level clears the narrower ones so stale children can't linger.
  const handleL1Change = (val: number | undefined) => {
    onCategoryL1Change(val);
    onCategoryL2Change(undefined);
    onCategoryL3Change(undefined);
  };

  const handleL2Change = (val: number | undefined) => {
    onCategoryL2Change(val);
    onCategoryL3Change(undefined);
  };

  return (
    <div className="flex flex-wrap gap-3 items-center">
      <div className="relative flex-1 min-w-[200px]">
        <svg
          className="absolute start-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <input
          type="text"
          placeholder={t("assets.searchPlaceholder")}
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="w-full ps-10 pe-11 py-2 border border-border rounded-lg text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
          data-testid="asset-search"
        />
        <Tooltip content={t("assets.resetFilters")} className="absolute end-1.5 top-1/2 -translate-y-1/2">
          <button
            type="button"
            onClick={onReset}
            aria-label={t("assets.resetFilters")}
            className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
            data-testid="asset-filters-reset"
          >
            <RefreshCw className="w-4 h-4" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>

      <select
        value={categoryL1Id ?? ""}
        onChange={(e) => handleL1Change(e.target.value ? Number(e.target.value) : undefined)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-category-l1"
      >
        <option value="">{t("assets.allCategoryGroups")}</option>
        {l1s.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.name}
          </option>
        ))}
      </select>

      <select
        value={categoryL2Id ?? ""}
        onChange={(e) => handleL2Change(e.target.value ? Number(e.target.value) : undefined)}
        disabled={!categoryL1Id}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
        data-testid="asset-filter-category-l2"
      >
        <option value="">{t("assets.allCategoryTypes")}</option>
        {l2s.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.name}
          </option>
        ))}
      </select>

      <select
        value={categoryL3Id ?? ""}
        onChange={(e) => onCategoryL3Change(e.target.value ? Number(e.target.value) : undefined)}
        disabled={!categoryL2Id}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary disabled:bg-muted disabled:text-muted-foreground disabled:cursor-not-allowed"
        data-testid="asset-filter-category-l3"
      >
        <option value="">{t("assets.allCategoryModels")}</option>
        {l3s.map((cat) => (
          <option key={cat.id} value={cat.id}>
            {cat.name}
          </option>
        ))}
      </select>

      <select
        value={status !== undefined ? String(status) : ""}
        onChange={(e) =>
          onStatusChange(e.target.value !== "" ? (e.target.value as AssetStatus) : undefined)
        }
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-status"
      >
        <option value="">{t("assets.allStatuses")}</option>
        <option value={AssetStatus.Available}>{t("assets.statusAvailable")}</option>
        <option value={AssetStatus.Issued}>{t("assets.statusIssued")}</option>
        <option value={AssetStatus.UnderMaintenance}>{t("assets.statusUnderMaintenance")}</option>
        <option value={AssetStatus.Retired}>{t("assets.statusRetired")}</option>
      </select>

      <select
        value={condition !== undefined ? String(condition) : ""}
        onChange={(e) =>
          onConditionChange(
            e.target.value !== "" ? (e.target.value as AssetCondition) : undefined
          )
        }
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-condition"
      >
        <option value="">{t("assets.allConditions")}</option>
        <option value={AssetCondition.Excellent}>{t("assets.conditionExcellent")}</option>
        <option value={AssetCondition.Good}>{t("assets.conditionGood")}</option>
        <option value={AssetCondition.Fair}>{t("assets.conditionFair")}</option>
        <option value={AssetCondition.Poor}>{t("assets.conditionPoor")}</option>
      </select>

      <select
        value={locationId ?? ""}
        onChange={(e) => onLocationChange(e.target.value ? Number(e.target.value) : undefined)}
        className="px-3 py-2 border border-border rounded-lg text-sm bg-card focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
        data-testid="asset-filter-location"
      >
        <option value="">{t("assets.allLocations")}</option>
        {locations.map((loc) => (
          <option key={loc.id} value={loc.id}>
            {loc.name}
          </option>
        ))}
      </select>
    </div>
  );
};

export default AssetFilters;
